import { breakpoint } from '@porsche-design-system/components-js/styles';
import { Component, Element, Event, EventEmitter, h, Host, Listen, Prop, State, Watch } from '@stencil/core';
import {
  findTopLevelRoutingKey,
  getLastRoutingSegment,
  RoutingKey,
  RoutingKeyPointers,
  RoutingKeys,
  RoutingKeyWithClick
} from '../../entities/routing-key';
import animationManager from '../../services/animations/animation-manager';
import featureToggles from '../../services/feature-toggles';
import windowService from '../../services/window-service';
import { dealerSearchResultsStore } from '../../state/dealer-search-results-store';
import { routerStore } from '../../state/router-store';
import {
  AnimationSequences,
  FEATURE_PORSCHE_75_YEARS_LOGO,
  NavigationMode,
  NavigationModeType,
  NavigationTheme,
  NavigationThemeType
} from '../../utility/constants';
import { Application, ApplicationType } from '../../utility/constants/app';
import {
  constructThemeClass,
  focusOnElement,
  isDesktop,
  IsKeyboardClick,
  isPromise,
  isString
} from '../../utility/helper';
import { isShopAvailable } from '../../utility/helpers/shop-helper';
import { componentsOnReady } from '../../utility/ready';
import { Timeout } from '../../utility/timeout';
import { isPlayingDoubleDrawerAnimation } from '../double-drawer/double-drawer-animation-sequences';
import { isPlayingSingleDrawerAnimation } from '../single-drawer/single-drawer-animation-sequences';

@Component({
  tag: 'phn-level-0',
  styleUrl: 'level-0.scss',
  scoped: true
})
export class Level0 {
  @Prop() locale!: string;
  @Prop() breakpoint!: number;
  @Prop() loadingContent = false;
  @Prop() receivedContent = false;
  @Prop() theme: NavigationThemeType = NavigationTheme.light;
  @Prop() mode: NavigationModeType = NavigationMode.navbar;
  @Prop() app: ApplicationType = Application.default;
  @Prop() displayLogoOnly = false;
  @Prop() limitedWidth = false;

  @State() usingKeyboard = false;
  @State() drawerClosed = true;
  @State() scrolledPastHeader = false;
  @State() showBackdrop = false;
  @State() routingKeyHistory: string[] = [];
  @State() routingKeyPointer = RoutingKeyPointers.LEVEL_ZERO;
  @State() isNavigationLoaded = false;
  @State() isLoading = false;
  @State() isMyPorscheHoveredOn = false;

  @Element() el!: HTMLPhnLevel0Element;

  @Event() navigationLoaded!: EventEmitter<boolean>;

  private operationPipeline: ((() => Promise<void>) | (() => void))[] = [];

  private logoLoaded = false;
  private drawerTimeout = new Timeout(20000);

  @Watch('loadingContent')
  loadingContentWatcher(): void {
    this.setNavigationLoaded();
  }

  @Watch('breakpoint')
  breakpointWatcher(): void {
    this.updateRoutingKeyPointerOfMobileLevel1();
    this.setNavigationLoaded();
    this.handleResizeAnimation();
  }

  private handleResizeAnimation(): void {
    if (!isDesktop(this.breakpoint) && !this.drawerClosed) {
      this.operationPipeline.push(() => {
        animationManager.play(AnimationSequences.OPEN_SINGLE_DRAWER);
      });
    }
  }

  @Watch('receivedContent')
  receivedContentWatcher(): void {
    this.setNavigationLoaded();
  }

  @Listen('logoLoaded')
  logoLoadedHandler(): void {
    this.logoLoaded = true;
    this.setNavigationLoaded();
  }

  @Listen('scroll', { target: 'window', passive: true })
  scrollHandler(): void {
    this.scrolledPastHeader = this.el.getBoundingClientRect().bottom <= 0;
  }

  @Listen('keydown', { target: 'window', passive: true })
  handleKeyDown(e: KeyboardEvent): void {
    if (e.key === 'Escape' && !this.drawerClosed) {
      this.usingKeyboard = true;
      this.handleClickDrawerHeaderClose();
      return;
    }
  }

  @Listen('click', { passive: true })
  handleClick(e: MouseEvent): void {
    this.usingKeyboard = IsKeyboardClick(e);
  }

  @Listen('mobileMenuItemClick')
  handleMobileMenuItemClick({ detail: newRoutingKeys }: CustomEvent<string[]>): void {
    this.routingKeyHistory = [...this.routingKeyHistory.slice(0, this.routingKeyPointer + 1), ...newRoutingKeys];
    this.routingKeyPointer = this.routingKeyPointer + newRoutingKeys.length;
    this.updateRouterStore();
  }

  @Listen('sideDrawerItemClick')
  handleSideDrawerItemClick({ detail: routingKeys }: CustomEvent<string[]>): void {
    this.routingKeyHistory = [...this.routingKeyHistory.slice(0, this.routingKeyPointer), ...routingKeys];
    if (routingKeys.length > 0) {
      this.routingKeyPointer = this.routingKeyPointer + routingKeys.length - 1;
    }
    this.updateRouterStore();
  }

  @Listen('clickDrawerHeaderBack')
  handleClickDrawerHeaderBack(): void {
    this.routingKeyPointer = this.routingKeyPointer - 1;
    this.updateRouterStore();
  }

  @Listen('clickDrawerHeaderClose')
  handleClickDrawerHeaderClose(): void {
    if (isPlayingSingleDrawerAnimation() || isPlayingDoubleDrawerAnimation()) {
      return;
    }

    this.routingKeyPointer = -1;
    this.updateRouterStore();

    if (isDesktop(this.breakpoint)) {
      animationManager.play(AnimationSequences.CLOSE_DOUBLE_DRAWER).then(() => {
        this.operationPipeline.push(() => {
          this.handleDrawerClosed();
        });
      });
    } else {
      this.operationPipeline.push(() => {
        animationManager.play(AnimationSequences.CLOSE_SINGLE_DRAWER).then(() => {
          this.operationPipeline.push(() => {
            this.handleDrawerClosed();
          });
        });
      });
    }
  }

  @Listen('backdropClosed')
  handleBackdropClosed(): void {
    this.showBackdrop = false;
  }

  @Listen('drawerChanged')
  handleDrawerChanged({ detail: isNavigatingBackwards }: CustomEvent<boolean>): void {
    const activeRoutingKey = isNavigatingBackwards
      ? this.routingKeyHistory[this.routingKeyPointer + 1]
      : this.routingKeyHistory[this.routingKeyPointer];

    const topLevelRoutingKey = findTopLevelRoutingKey(activeRoutingKey ?? '');

    if (topLevelRoutingKey !== RoutingKeys.DEALER_SEARCH || topLevelRoutingKey === activeRoutingKey) {
      return;
    }

    if (isNavigatingBackwards) {
      dealerSearchResultsStore.state.savedDealerCookie = null;
      return;
    }

    const dealerId = getLastRoutingSegment(activeRoutingKey);
    const { ppnDealer: savedDealer } = dealerSearchResultsStore.state.ppnDealers[dealerId] || {};

    if (savedDealer !== undefined) {
      dealerSearchResultsStore.state.savedDealerCookie = {
        id: savedDealer.id,
        latitude: savedDealer.coordinates.latitude,
        longitude: savedDealer.coordinates.longitude
      };
    }
  }

  @Listen('openDrawer')
  async handleOpenDrawer(event: CustomEvent<RoutingKeyWithClick>): Promise<void> {
    await this.openDrawer(event.detail);
  }

  private updateRouterStore(): void {
    routerStore.state.routingKeyHistory = this.routingKeyHistory;
    routerStore.state.routingKeyPointer = this.routingKeyPointer;
  }

  private handleDrawerClosed(): void {
    this.drawerClosed = true;

    if (this.usingKeyboard) {
      focusOnElement(this.el.querySelector('phn-burger-button phn-p-button-pure'));
    }
  }

  private updateRoutingKeyPointerOfMobileLevel1() {
    if (this.routingKeyPointer === RoutingKeyPointers.LEVEL_ONE) {
      this.routingKeyPointer = isDesktop(this.breakpoint) ? RoutingKeyPointers.LEVEL_TWO : RoutingKeyPointers.LEVEL_ONE;
    }
  }

  private setNavigationLoaded(): void {
    this.isNavigationLoaded = !this.loadingContent && this.logoLoaded;
    this.navigationLoaded.emit(this.isNavigationLoaded);
  }

  private setInitialRoutingProperties(initialRoutingKey: RoutingKey | string) {
    if (isString(initialRoutingKey)) {
      this.setRoutingProperties([initialRoutingKey]);
    } else if (routerStore.state.initialRoutingKeyHistory !== null) {
      this.setRoutingProperties(routerStore.state.initialRoutingKeyHistory);
    } else {
      this.setRoutingProperties([RoutingKeys.MODELS]);
    }
  }

  private async openDrawer(routingKeyWithClick: RoutingKeyWithClick): Promise<void> {
    const { initialRoutingKey, isKeyboardClick } = routingKeyWithClick;

    if (!this.drawerClosed) {
      return;
    }

    this.isLoading = true;
    this.usingKeyboard = isKeyboardClick;
    this.drawerClosed = false;
    this.showBackdrop = true;

    this.setInitialRoutingProperties(initialRoutingKey);

    await componentsOnReady(this.el, this.drawerTimeout, '.drawer', 'phn-backdrop');
    this.isLoading = false;

    if (isDesktop(this.breakpoint)) {
      animationManager.play(AnimationSequences.OPEN_DOUBLE_DRAWER);
    } else {
      this.operationPipeline.push(() => {
        animationManager.play(AnimationSequences.OPEN_SINGLE_DRAWER);
      });
    }
  }

  private isDefaultRouteOnMobile(): boolean {
    return (
      !isDesktop(this.breakpoint) &&
      routerStore.state.initialRoutingKeyHistory === null &&
      this.routingKeyHistory.length === 2 &&
      this.routingKeyHistory[1] === RoutingKeys.MODELS
    );
  }

  private setRoutingProperties(initialRoutingKeys: string[]) {
    this.routingKeyHistory = [RoutingKeys.MAIN_MENU, ...initialRoutingKeys];
    this.routingKeyPointer = isDesktop(this.breakpoint) ? RoutingKeyPointers.LEVEL_TWO : RoutingKeyPointers.LEVEL_ONE;

    if (!this.isDefaultRouteOnMobile()) {
      this.routingKeyPointer = this.routingKeyHistory.length - 1;
    }

    this.updateRouterStore();
  }

  disconnectedCallback(): void {
    this.drawerTimeout.cancel();
  }

  async componentDidRender(): Promise<void> {
    const promises = [];
    for (const operation of this.operationPipeline) {
      const maybePromise = operation();
      if (isPromise<void>(maybePromise)) {
        promises.push(maybePromise);
      }
    }
    await Promise.all(promises);
    this.operationPipeline.splice(0, this.operationPipeline.length);
  }

  private getLogoSize(): string {
    if (featureToggles.isFeatureEnabled(FEATURE_PORSCHE_75_YEARS_LOGO, windowService.location.search)) {
      return 'medium';
    }
    return this.breakpoint >= breakpoint.m ? 'medium' : 'small';
  }

  render(): HTMLPhnLevel0Element {
    const renderBackdrop = this.showBackdrop;

    return (
      <Host class={`${constructThemeClass(this.mode, this.theme)}-theme themed-background`}>
        <header class={this.limitedWidth ? 'limited-width' : ''}>
          {renderBackdrop ? (
            <phn-backdrop
              activeRoutingKey={this.routingKeyHistory[this.routingKeyPointer]}
              style={this.isLoading ? { display: 'none' } : { backgroundColor: 'rgba(0, 0, 0, 0)', opacity: '0' }}
            />
          ) : null}

          <div class="level-0-content">
            {!this.displayLogoOnly ? (
              <div class="level-0-content-icons">
                <phn-level-0-icons
                  locale={this.locale}
                  theme={this.theme}
                  mode={this.mode}
                  app={this.app}
                  breakpoint={this.breakpoint}
                  isLoading={this.isLoading}
                  isNavigationLoaded={this.isNavigationLoaded}
                >
                  <div class="level-0-content-logo">
                    <phn-logo
                      app={this.app}
                      locale={this.locale}
                      theme={this.theme}
                      mode={this.mode}
                      size={this.getLogoSize()}
                      breakpoint={this.breakpoint}
                      style={this.isNavigationLoaded ? {} : { display: 'none' }}
                    />
                    {!this.isNavigationLoaded ? (
                      <div class="crest-skeleton skeleton-container">
                        {this.breakpoint > breakpoint.xs ? <span class="label-bone" /> : <span class="crest-bone" />}
                      </div>
                    ) : null}
                  </div>
                </phn-level-0-icons>
                {isShopAvailable(this.locale, this.app) ? (
                  <contextual-drawer>
                    <div slot="contextual_drawer_header">
                      <slot name="contextual_drawer_header"></slot>
                    </div>
                  </contextual-drawer>
                ) : null}
              </div>
            ) : (
              <div class="level-0-content-logo">
                <phn-logo
                  app={this.app}
                  locale={this.locale}
                  theme={this.theme}
                  mode={this.mode}
                  size={this.getLogoSize()}
                  breakpoint={this.breakpoint}
                  style={this.isNavigationLoaded ? {} : { display: 'none' }}
                />
                {!this.isNavigationLoaded ? (
                  <div class="crest-skeleton skeleton-container">
                    {this.breakpoint > breakpoint.xs ? <span class="label-bone" /> : <span class="crest-bone" />}
                  </div>
                ) : null}
              </div>
            )}
          </div>

          {!this.drawerClosed ? (
            isDesktop(this.breakpoint) ? (
              <phn-double-drawer
                class="drawer"
                style={this.isLoading ? { display: 'none' } : {}}
                routingKeyHistory={this.routingKeyHistory}
                routingKeyPointer={this.routingKeyPointer}
                breakpoint={this.breakpoint}
                initialUsingKeyboard={this.usingKeyboard}
                locale={this.locale}
                receivedContent={this.receivedContent}
              />
            ) : (
              <phn-single-drawer
                class="drawer"
                style={this.isLoading ? { display: 'none' } : {}}
                routingKeyHistory={this.routingKeyHistory}
                routingKeyPointer={this.routingKeyPointer}
                breakpoint={this.breakpoint}
                usingKeyboard={this.usingKeyboard}
                locale={this.locale}
                receivedContent={this.receivedContent}
              />
            )
          ) : null}
        </header>
      </Host>
    );
  }
}
